/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/camelcase */
import React from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Grid, IconButton, makeStyles, Tooltip } from '@material-ui/core';
import { Input as InputIcon } from '@material-ui/icons';
import i18n from 'i18n';
import Button from 'components/Button';
import Breadcrumbs from 'components/Breadcrumbs';
import PageTitle from 'components/PageTitle';
import RefreshButton from 'components/RefreshButton';
import { isClientOperator } from 'utils/role';
import TableItems, {
  MenuActionItem,
  MenuActions,
  StatusIcon,
  TableCell,
  TableRow,
} from 'components/TableItems';
import Permission from 'components/ValidPermission';
import { useTableFilters, Query } from 'hooks/useTableFilters';
import {
  CompanyData,
  PartnerData,
  PermissionsData,
  RolesEnum,
} from 'modules/authenticate/interfaces';
import {
  activeUserInClientRequest,
  disableUserInClientRequest,
  editClientUserRequest,
  removeClientUserRequest,
} from 'modules/clients/actions';
import { getAllUsersRequest } from 'modules/users/actions';
import {
  GetAllUsersFields as UsersFields,
  ListAllUsersDataProps as ListUsersData,
} from 'modules/users/interfaces';
import { modalClose, modalOpen } from 'modules/modal/actions';
import { ModalFields } from 'modules/modal/interfaces';
import ApplicationState from 'store/interfaces';
import { formatDateValue } from 'utils/dateFormat';
import { cyrb53Hash } from 'utils/helpers';
import { SearchBar } from 'components/SearchBar';
import getHeaders from './headers';
import FormUser from './FormUser';
import styles from './styles';
import UserSession from './Sessions';

interface UsersListProps {
  companySelected: CompanyData | null;
  isLoadingUsers: boolean;
  usersList: ListUsersData;
  partnerSelected: PartnerData | null;
  permissions?: PermissionsData;
  role?: RolesEnum;
  activeUserInClientRequest: ({ userUuid, id, query, isListAllUsers }) => void;
  disableUserInClientRequest: ({ userUuid, id, query, isListAllUsers }) => void;
  editClientUserRequest: (props: {
    clientUuid: string;
    userUuid: string;
    values: any;
    isListAllUsers: boolean;
    query?: Query;
  }) => void;
  getAllUsersRequest: (props) => void;
  modalClose: () => void;
  modalOpen: (props: ModalFields) => void;
  removeClientUserRequest: (props: {
    clientUuid: string;
    userUuid: string;
    isListAllUsers: boolean;
    query?: Query;
  }) => void;
}

const UsersList = ({
  companySelected,
  isLoadingUsers,
  usersList,
  partnerSelected,
  permissions,
  role,
  activeUserInClientRequest,
  disableUserInClientRequest,
  editClientUserRequest,
  getAllUsersRequest,
  modalClose,
  modalOpen,
  removeClientUserRequest,
}: UsersListProps) => {
  const classes = makeStyles(styles)();
  const history = useHistory();
  const {
    itemOrder,
    query,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters();

  const [refresh, setRefresh] = React.useState(false);

  const getUsers = (refresh = false) => {
    getAllUsersRequest({
      ...query,
    });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    if (
      (companySelected && partnerSelected) ||
      permissions?.can_view_all_resources
    ) {
      setQuery(prev => ({
        ...prev,
        company_uuid: permissions?.can_view_all_resources
          ? ''
          : companySelected?.id,
        partner_uuid: permissions?.can_view_all_resources
          ? ''
          : partnerSelected?.id,
      }));
    }
  }, [companySelected, partnerSelected]);

  React.useEffect(() => {
    getUsers();
  }, [query]);

  const openModalActivateUser = (item: UsersFields) =>
    modalOpen({
      title: i18n.t('WANT_ACTIVATE_USERS_KEY'),
      content: i18n.t('ACTIVE_USER_KEY', {
        id: `${item?.first_name} ${item?.last_name}`,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              activeUserInClientRequest({
                userUuid: item?.user_uuid,
                id: item?.client.client_uuid,
                query,
                isListAllUsers: true,
              });
              modalClose();
              setRefresh(false);
            }}
          >
            {i18n.t('ACTIVATE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const openModalDeactivateUser = (item: UsersFields) =>
    modalOpen({
      title: i18n.t('WANT_DISABLE_USER_KEY'),
      content: i18n.t('DISABLE_USER_DESCRIPTION_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              disableUserInClientRequest({
                userUuid: item?.user_uuid,
                id: item?.client.client_uuid,
                query,
                isListAllUsers: true,
              });
              modalClose();
              setRefresh(false);
            }}
          >
            {i18n.t('DISABLE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  return (
    <Grid container>
      <Grid container alignItems="center" justify="space-between">
        <PageTitle
          title={i18n.t('LIST_USERS')}
          subtitle={
            <Breadcrumbs
              values={[
                {
                  name: i18n.t('ENVIRONMENTS_KEY'),
                  link: '/environments',
                },
                {
                  name: i18n.t('CLIENTS_KEY'),
                  link: '/clients',
                },
                {
                  name: i18n.t('USERS_KEY'),
                },
              ]}
            />
          }
        />
        <RefreshButton
          id="btn-refresh"
          onClick={() => getUsers(true)}
          animate={isLoadingUsers}
          disabled={isLoadingUsers}
        />
      </Grid>
      <Grid container>
        <SearchBar
          id="search-users"
          isLoading={isLoadingUsers}
          handleSearch={handleSearch}
          setSearchValue={setSearchValue}
        />
        <TableItems
          header={
            <>
              {getHeaders().map(item => (
                <TableCell
                  centerTitle={item?.centerTitle}
                  key={item?.key}
                  order={item?.order}
                  markOderItem={itemOrder === item?.key}
                  handleOrder={() =>
                    item?.order ? handleOrder(item?.key) : null
                  }
                >
                  {i18n.t(item?.i18n)}
                </TableCell>
              ))}
            </>
          }
          renderItems={(item: UsersFields, index) => (
            <TableRow
              key={cyrb53Hash(item?.environment?.environment_name + index)}
            >
              <TableCell
                maxLength={10}
                ellipsis
                centerTitle
                className={classes.centerAccess}
              >
                <IconButton
                  id={`access-users-list-${index}`}
                  onClick={() => {
                    history.push(`/clients/${item?.client?.client_uuid}/users`);
                  }}
                >
                  <InputIcon className={classes.accessButton} />
                </IconButton>
              </TableCell>
              <TableCell
                className={classes.centerFields}
                tooltip={`${item?.first_name} ${item?.last_name}`}
              >
                {`${item?.first_name} ${item?.last_name}`}
              </TableCell>
              <TableCell
                className={classes.emailField}
                ellipsis
                tooltip={item?.email}
              >
                {item?.email}
              </TableCell>
              <TableCell
                className={classes.centerLongFields}
                tooltip={item?.client?.client_name}
                ellipsis
              >
                {item?.client?.client_name}
              </TableCell>
              <TableCell
                className={classes.centerLongFields}
                tooltip={item?.environment?.environment_name}
                ellipsis
              >
                {item?.environment?.environment_name}
              </TableCell>
              <TableCell
                className={classes.centerSmallFields}
                tooltip={i18n.t(item?.active ? 'ACTIVE_KEY' : 'INACTIVE_KEY')}
              >
                <Grid container justify="center">
                  <StatusIcon status={item?.active ? 'success' : 'inactive'} />
                </Grid>
              </TableCell>
              <TableCell>
                <Grid container justify="flex-start" spacing={1}>
                  <Grid item>
                    <Tooltip
                      arrow
                      title={
                        item?.mfa_configured
                          ? i18n.t('USER_MFA_ACTIVE_TOOLTIP')
                          : i18n.t('USER_MFA_INACTIVE_TOOLTIP')
                      }
                    >
                      {item?.mfa_configured ? (
                        <img
                          src="/images/mfaEnable.svg"
                          alt="Logo"
                          height={20}
                        />
                      ) : (
                        <img
                          src="/images/mfaDisable.svg"
                          alt="Logo"
                          height={20}
                        />
                      )}
                    </Tooltip>
                  </Grid>
                  {!isClientOperator(role) && (
                    <Grid item>
                      <Tooltip
                        arrow
                        title={
                          item?.salt_configured
                            ? i18n.t('ACTIVE_SALT_KEY')
                            : i18n.t('INACTIVE_SALT_KEY')
                        }
                      >
                        {item?.salt_configured ? (
                          <img
                            src="/images/saltEnable.svg"
                            alt="Logo"
                            height={20}
                          />
                        ) : (
                          <img
                            src="/images/saltDisable.svg"
                            alt="Logo"
                            height={20}
                          />
                        )}
                      </Tooltip>
                    </Grid>
                  )}

                  <Grid item>
                    {item?.environment.device_validation_enabled && (
                      <Tooltip arrow title={i18n.t('DEVICE_VALIDATION_KEY')}>
                        <img
                          src="/images/safeDeviceEnable.svg"
                          alt="Logo"
                          height={20}
                        />
                      </Tooltip>
                    )}
                  </Grid>
                </Grid>
              </TableCell>
              <TableCell centerTitle className={classes.centerFields}>
                {formatDateValue(item?.last_access)}
              </TableCell>
              <TableCell textAlignCenter>
                <MenuActions id={`btn-options-users-${index}`}>
                  <Permission
                    value={item?.active ? 'inactivate_user' : 'activate_user'}
                  >
                    <MenuActionItem
                      id="btn-state-user"
                      onClick={() =>
                        item?.active
                          ? openModalDeactivateUser(item)
                          : openModalActivateUser(item)
                      }
                    >
                      {item?.active
                        ? i18n.t('DISABLE_KEY')
                        : i18n.t('ACTIVATE_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="edit_user_details">
                    <MenuActionItem
                      id="btn-edit-user"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('EDIT_USER_KEY'),
                          content: (
                            <FormUser
                              defaultValues={item}
                              // eslint-disable-next-line @typescript-eslint/no-unused-vars
                              onSubmit={({ username, ...values }) => {
                                editClientUserRequest({
                                  clientUuid: item?.client?.client_uuid,
                                  userUuid: item?.user_uuid,
                                  values,
                                  isListAllUsers: true,
                                  query,
                                });
                                modalClose();
                              }}
                              onCancel={modalClose}
                            />
                          ),
                          maxWidth: 'sm',
                        })
                      }
                    >
                      {i18n.t('EDIT_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="list_environment_instance_sessions">
                    <MenuActionItem
                      id="btn-sessions"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('SESSIONS_KEY'),
                          content: <UserSession user={item} />,
                          genericFooter: { action: modalClose },
                          maxWidth: 'md',
                        })
                      }
                    >
                      {i18n.t('SESSIONS_KEY')}
                    </MenuActionItem>
                  </Permission>
                  <Permission value="delete_user">
                    <MenuActionItem
                      id="btn-remove-user"
                      onClick={() =>
                        modalOpen({
                          title: i18n.t('WANT_REMOVE_USER_KEY', {
                            name: `${item?.first_name} ${item?.last_name}`,
                          }),
                          content: i18n.t('REMOVE_USER_DESCRIPTION_KEY'),
                          footer: (
                            <Grid>
                              <Button
                                submitButton
                                id="submit-button"
                                onClick={() => {
                                  removeClientUserRequest({
                                    clientUuid: item?.client.client_uuid,
                                    userUuid: item?.user_uuid,
                                    isListAllUsers: true,
                                    query,
                                  });
                                  modalClose();
                                }}
                              >
                                {i18n.t('REMOVE_KEY')}
                              </Button>
                              <Button
                                id="cancel-button"
                                variant="outlined"
                                onClick={modalClose}
                              >
                                {i18n.t('CANCEL_KEY')}
                              </Button>
                            </Grid>
                          ),
                        })
                      }
                    >
                      {i18n.t('USERS_PAGE.DELETE_BUTTON_KEY')}
                    </MenuActionItem>
                  </Permission>
                </MenuActions>
              </TableCell>
            </TableRow>
          )}
          data={usersList}
          isLoading={!refresh && isLoadingUsers}
          onChangePage={handlePageChange}
          onChangeLimit={handleLimitChange}
          notFoundText={i18n.t('USERS_PAGE.NO_USERS_KEY')}
          alertText={
            (!companySelected || !partnerSelected) &&
            !permissions?.can_view_all_resources
              ? i18n.t('TO_LIST_SELECT_COMPANY_AND_PARTNER_KEY')
              : null
          }
        />
      </Grid>
    </Grid>
  );
};

const mapStateToProps = ({
  authenticate: { companySelected, data, partnerSelected },
  users: { allUsersList, isLoadingAllUsersList },
}: ApplicationState) => ({
  companySelected,
  isLoadingUsers: isLoadingAllUsersList,
  partnerSelected,
  usersList: allUsersList,
  role: data?.role,
  permissions: data?.permissions,
});

export default connect(mapStateToProps, {
  activeUserInClientRequest,
  disableUserInClientRequest,
  editClientUserRequest,
  getAllUsersRequest,
  modalClose,
  modalOpen,
  removeClientUserRequest,
})(UsersList);
